// ============================================================================
// Project Feature Types
// ============================================================================

import type { api } from "@repo/convex/api";
import type { Doc } from "@repo/convex/dataModel";
import type { ConversationStatus, ConversationStatusReason } from "@repo/shared";
import type { FunctionReturnType } from "convex/server";

// Tab search params stored in context
export type TabSearchParams = Record<string, unknown>;

// Right sidebar tab types
export type RightSidebarTab = "versions" | "guardian" | "cr" | "teams" | "claudia";

// Clarification Request Filter Types
export type ClarificationRequestFilter = "open" | "all" | "closed";
export type ClarificationViewMode = "list" | "thread";

export interface ClarificationIndicatorLinkState {
  clarificationIds: string[];
  primaryClarificationId: string | null;
  activationKey: number;
}

// ============================================================================
// Claudia render contract
//
// Every type below is DERIVED from the Convex query that produces it, via
// `FunctionReturnType`. Do not restate a backend shape here as a hand-written
// interface: a mirror compiles happily while the two sides drift, and the
// drift only shows up as a blank card at runtime. See
// `apps/web-app/AGENTS.md` § Convex from the browser.
// ============================================================================

/**
 * The Claudia composer's live runtime subscription (`claudia.getThreadRuntimeSnapshot`): prompt
 * lifecycle, the run the send/stop control acts on, and the context-usage badge.
 */
export type AssistantThreadRuntimeSnapshot = NonNullable<
  FunctionReturnType<typeof api.claudia.getThreadRuntimeSnapshot>
>;

/**
 * The run shape the snapshot carries — run-row fields plus two derived control booleans,
 * deliberately without tool calls or artifacts so the subscription stays small.
 */
export type AssistantRuntimeRunSummary = NonNullable<AssistantThreadRuntimeSnapshot["activeRun"]>;

export type AssistantRunStatus = AssistantRuntimeRunSummary["status"];

export type AssistantApprovalStatus = AssistantRuntimeRunSummary["approvalStatus"];

export type AssistantRunCompletionMode = NonNullable<AssistantRuntimeRunSummary["completionMode"]>;

export type AssistantPromptExecutionRuntime = NonNullable<
  AssistantThreadRuntimeSnapshot["promptStatus"]
>;

export type AssistantPromptExecutionPhase = AssistantPromptExecutionRuntime["phase"];

export type AssistantContextUsageEstimate = NonNullable<
  AssistantThreadRuntimeSnapshot["contextUsage"]
>;

/** The whole transcript envelope for one window of a thread. */
export type AssistantTranscriptTimelineQueryResult = FunctionReturnType<
  typeof api.claudia.listTranscriptTimeline
>;

/**
 * A row as the transcript renders it: every field comes from the projection, but the two identity
 * stamps the *server* applies (`withTranscriptIdentity`) are optional here.
 *
 * The rendered list is a merge of three inputs — the two timeline windows, the vendored message
 * stream, and this session's optimistic rows — and only the first is stamped. A streaming row has
 * no `transcriptItemKey` until it settles, and the merge reads that absence to tell an unstamped
 * echo of the user's own submission from the persisted message that replaces it.
 */
export type AssistantTranscriptTimelineItem = LooseTranscriptIdentity<
  AssistantTranscriptTimelineQueryResult["items"][number]
>;

type LooseTranscriptIdentity<TItem> = TItem extends unknown
  ? Omit<TItem, "transcriptItemKey" | "finality"> & {
      transcriptItemKey?: string;
      finality?: "streaming" | "final";
    }
  : never;

export type AssistantTranscriptMessageTimelineItem = Extract<
  AssistantTranscriptTimelineItem,
  { kind: "message" }
>;

/** Everything the transcript renders that is not a chat message. */
export type AssistantTranscriptDecorationItem = Exclude<
  AssistantTranscriptTimelineItem,
  { kind: "message" }
>;

export type AssistantActivityTurnTimelineItem = Extract<
  AssistantTranscriptTimelineItem,
  { kind: "activity_turn" }
>;

export type AssistantRunControlTimelineItem = Extract<
  AssistantTranscriptTimelineItem,
  { kind: "run_control" }
>;

export type AssistantContextCompactionTimelineItem = Extract<
  AssistantTranscriptTimelineItem,
  { kind: "context_compaction" }
>;

export type AssistantOperationApprovalTimelineItem = Extract<
  AssistantTranscriptTimelineItem,
  { kind: "operation_approval" }
>;

export type AssistantAskUserTimelineItem = Extract<
  AssistantTranscriptTimelineItem,
  { kind: "assistant_ask_user" }
>;

export type AssistantActivityTurnStatus = AssistantActivityTurnTimelineItem["status"];

export type AssistantActivityTurnSegmentKind = AssistantActivityTurnTimelineItem["segmentKind"];

export type AssistantActivityPauseInterval =
  AssistantActivityTurnTimelineItem["pauseIntervals"][number];

export type AssistantActivityPauseKind = AssistantActivityPauseInterval["kind"];

export type AssistantActivityTurnItem = AssistantActivityTurnTimelineItem["items"][number];

export type AssistantActivityTurnItemKind = AssistantActivityTurnItem["kind"];

export type AssistantActivityTurnItemStatus = AssistantActivityTurnItem["status"];

export type AssistantActivityTurnBlock = NonNullable<
  AssistantActivityTurnTimelineItem["blocks"]
>[number];

/**
 * One run of an activity label. `mention` is the canonical reference part — the same token the chat
 * transcript renders as a chip, resolved to a route on activation. `link` survives for places that
 * are not entities (synthetic locations) and for labels persisted before mentions existed.
 */
export type AssistantLinkedTextPart = NonNullable<AssistantActivityTurnItem["textParts"]>[number];

export type AssistantActivityTurnApprovalPayload = NonNullable<
  AssistantActivityTurnItem["approval"]
>;

export type AssistantActivityTurnToolPayload = NonNullable<
  AssistantActivityTurnItem["toolActivity"]
>;

export type AssistantActivityTurnContextCompactionPayload = NonNullable<
  AssistantActivityTurnItem["contextCompaction"]
>;

export type AssistantActivityTurnUserInputPayload = NonNullable<
  AssistantActivityTurnItem["userInput"]
>;

export type AssistantRunEventRuntime = NonNullable<AssistantActivityTurnItem["entries"]>[number];

export type AssistantRunEventDisplayState = AssistantRunEventRuntime["displayState"];

/**
 * A chat row as the vendored `useUIMessages` stream cache hands it over.
 *
 * Deliberately NOT derived: this is the only Claudia surface whose rows do not come from a typed
 * Convex query. `@convex-dev/agent`'s streaming hook is consumed through `unknown` casts (see
 * `use-claudia-transcript-messages.ts`), and a partially streamed row legitimately lacks most of
 * what the settled projection guarantees — which is exactly what `normalizeTranscriptMessage`
 * fills in before the row joins the derived timeline union.
 */
export interface AssistantTranscriptMessageRuntime {
  _creationTime?: number;
  _id?: string;
  id?: string;
  key?: string;
  transcriptItemKey?: string;
  finality?: "streaming" | "final";
  metadata?: unknown;
  threadId?: string;
  sourceOrder: number;
  promptMessageId?: string | null;
  /** Queue row this user message was drained from, when it arrived as queued input. */
  queuedInputId?: string;
  /** "queued": a steering message still waiting for the active run to read it (LAT-963). */
  deliveryState?: "queued";
  promptExecutionAttemptId?: string | null;
  promptAttemptNumber?: number | null;
  promptAttemptPhase?: string | null;
  sourceRunId?: string | null;
  messageKind?: "prompt_response";
  order: number;
  stepOrder: number;
  role: "user" | "assistant";
  status?: "pending" | "success" | "failed" | "streaming";
  text?: string;
  isStreaming?: boolean;
  parts: Array<
    | { type: "text"; text: string }
    | { type: "source-url"; sourceId: string; url: string; title?: string }
    | {
        type: "source-document";
        sourceId: string;
        artifactId: string;
        processingRevisionId: string;
        blockId: string;
        documentName: string;
        pageStart?: number;
        pageEnd?: number;
        headingPath: string[];
        excerpt: string;
      }
    | {
        type: "attachment";
        attachmentId: string;
        kind: "image" | "document";
        fileName: string;
        mimeType: string;
        fileSizeBytes: number;
        width?: number;
        height?: number;
        url: string | null;
        thumbnailUrl: string | null;
      }
  >;
}

type AssistantAskUserRequestDoc = Doc<"assistantAskUserRequests">;

export type AssistantAskUserQuestionRuntime = AssistantAskUserRequestDoc["questions"][number];

export type AssistantAskUserOptionRuntime = AssistantAskUserQuestionRuntime["options"][number];

export type AssistantAskUserAnswerMapRuntime = NonNullable<AssistantAskUserRequestDoc["answers"]>;

export type AssistantAskUserAnswerRuntime = AssistantAskUserAnswerMapRuntime[string];

/** The pending `ask_user` request the composer blocks on (`claudia.getPendingAskUserForThread`). */
export type AssistantAskUserRequestRuntime = NonNullable<
  FunctionReturnType<typeof api.claudia.getPendingAskUserForThread>
>;

/**
 * The question set and its saved draft — what the ask-user dock renders and drafts against.
 *
 * Narrow on purpose: the request row also names the org, project, prompt execution and tool call
 * it came from, and none of that reaches the screen. Asking for the whole row would make every
 * fixture restate provenance the dock never reads.
 */
export type AssistantAskUserPrompt = Pick<
  AssistantAskUserRequestRuntime,
  "requestId" | "status" | "questions" | "draftAnswers" | "answers" | "updatedAt"
>;

/**
 * One status axis for every conversation surface (nav flyout, panel tab, list screen), and the
 * reason line beneath it.
 *
 * Both are aliases of the shared vocabulary the backend derives them from — the members are
 * stated once in `@repo/shared` — and keep the names every conversation surface here imports.
 */
export type AssistantConversationStatus = ConversationStatus;

export type AssistantConversationReason = ConversationStatusReason;

/**
 * The conversation row as the backend projects it (`claudia.listProjectConversations`).
 *
 * Derived, not mirrored. A surface that wants to render rows without the live backend still
 * can: mock `api.claudia.*` the way `claudia-panel-ui.test.ts` does, or build a fixture row and
 * let this type check it. Restating the shape here only bought a copy that compiles after the
 * projection has already moved on.
 */
export type AssistantConversationListItem = FunctionReturnType<
  typeof api.claudia.listProjectConversations
>[number];

/** Client-authored: a narration line the transcript renders between backend rows. */
export interface AssistantTranscriptNarration {
  key: string;
  text: string;
  promptMessageId: string | null;
}

export interface OptimisticTranscriptExchange {
  localExchangeId: string;
  threadId: string;
  temporaryMessageId: string;
  prompt: string;
  attachments?: Array<
    Extract<AssistantTranscriptMessageTimelineItem["parts"][number], { type: "attachment" }>
  >;
  sourceOrder: number;
  order: number;
  createdAt: number;
  promptMessageId: string | null;
  runId: string | null;
  /**
   * What the backend did with this submission. "assistant_steering" means the message was absorbed
   * into the run that was already in flight, so the transcript shows a queued marker instead of a
   * new exchange until the run drains it.
   */
  pendingMode?: AssistantSubmitPendingMode | null;
  /** Queue row this submission became, when it was absorbed as steering. */
  queuedInputId?: string | null;
}

export type AssistantSubmitPendingMode =
  | "assistant_response"
  | "assistant_steering"
  | "assistant_ask_user";

// Tree item types for the navigation tree
export type TreeItemType =
  | "root-folder"
  | "project-tree-folder"
  | "entity-collection"
  | "objective"
  | "question"
  | "kpi"
  | "measure"
  | "hierarchy"
  | "project-link";

export interface TreeItemCreateParentEntity {
  alias: string;
  entityTypeKey: string;
  entityId: string;
  entitySlug: string;
}

export interface TreeItemMetadata {
  parentQuestionId?: string; // For KPIs under business questions
  frameworkNodeKey?: string;
  frameworkNodeKind?: "root" | "folder" | "collection" | "singleton";
  rootFolderKey?: string;
  rootFolderSlug?: string;
  entityTypeKey?: string;
  entityAlias?: string;
  entityId?: string;
  entitySlug?: string;
  collectionSlug?: string;
  collectionNodeKey?: string;
  projectTreeFolderId?: string;
  projectTreeFolderKind?: string;
  allowProjectTreeFolderCreate?: boolean;
  parentFrameworkNodeKey?: string;
  parentEntities?: TreeItemCreateParentEntity[];
}

// Tree item structure for navigation
export interface TreeItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path?: string; // Relative path within project
  type: TreeItemType;
  isKpi?: boolean;
  children?: TreeItem[];
  metadata?: TreeItemMetadata;
}

export interface TreeScrollRequest {
  nodeId: string;
  nonce: number;
}

// Tree expand state interface
export interface TreeExpandState {
  isNodeExpanded: (nodeId: string, defaultOpen: boolean) => boolean;
  toggleManualNode: (nodeId: string, defaultOpen: boolean) => void;
  expandManualNode: (nodeId: string) => void;
  collapseManualNode: (nodeId: string) => void;
  activateNode: (nodeId: string, locationKey: string) => void;
  syncFromLocation: (locationKey: string, targetNodeId: string | null) => void;
  manualCollapsedNodes: Set<string>;
  manualExpandedNodes: Set<string>;
  activeNodeId: string | null;
  activeLineageNodeIds: Set<string>;
  scrollRequest: TreeScrollRequest | null;
}

// Sidebar constants
export const LEFT_SIDEBAR_MIN = 200;
export const LEFT_SIDEBAR_MAX = 800;
export const LEFT_SIDEBAR_DEFAULT = 280;
export const RIGHT_SIDEBAR_MIN = 280;
export const RIGHT_SIDEBAR_MAX = 500;
export const RIGHT_SIDEBAR_DEFAULT = 320;

// Map short param values to full values
export const tabMap: Record<"v" | "g" | "cr" | "t" | "cl", RightSidebarTab> = {
  v: "versions",
  g: "guardian",
  cr: "cr",
  t: "teams",
  cl: "claudia",
};

export const tabMapReverse: Record<RightSidebarTab, "v" | "g" | "cr" | "t" | "cl"> = {
  versions: "v",
  guardian: "g",
  cr: "cr",
  teams: "t",
  claudia: "cl",
};

export const filterMap: Record<"o" | "a" | "c", ClarificationRequestFilter> = {
  o: "open",
  a: "all",
  c: "closed",
};

export const filterMapReverse: Record<ClarificationRequestFilter, "o" | "a" | "c"> = {
  open: "o",
  all: "a",
  closed: "c",
};

export const clarificationViewModeMap: Record<"l" | "t", ClarificationViewMode> = {
  l: "list",
  t: "thread",
};

export const clarificationViewModeMapReverse: Record<ClarificationViewMode, "l" | "t"> = {
  list: "l",
  thread: "t",
};

// Right sidebar tab configuration
export const rightSidebarTabs: {
  id: RightSidebarTab;
  label: string;
  icon: React.ElementType;
}[] = [];

// Note: rightSidebarTabs is populated at runtime to avoid circular dependency with icons
